import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import SimpleButton from "./SimpleButton";

interface ProcessingModalProps {
  show: boolean;
  title?: string;
  message?: string;
  onCancel: () => void;
}

export default function ProcessingModal({
  show,
  title = "Processing Content",
  message = "Please wait while we process your content. This may take a few minutes...",
  onCancel,
}: ProcessingModalProps) {
  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform rounded-2xl bg-white border-4 border-yellow shadow-2xl transition-all w-full max-w-md p-8 sm:p-10">
                {/* Content */}
                <div className="text-center">
                  {/* Animated Spinner */}
                  <div className="mx-auto flex items-center justify-center mb-6">
                    <div className="relative">
                      <ArrowPathIcon className="h-16 w-16 text-orange animate-spin" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="h-8 w-8 rounded-full bg-yellow opacity-20"></div>
                      </div>
                    </div>
                  </div>

                  {/* Title */}
                  <Dialog.Title as="h3" className="text-2xl sm:text-3xl font-bold text-dark-grey mb-4">
                    {title}
                  </Dialog.Title>

                  {/* Message */}
                  <p className="text-base sm:text-lg text-dark-grey mb-6 leading-relaxed">
                    {message}
                  </p>

                  {/* Progress Indicator */}
                  <div className="mb-6">
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <div className="h-2 w-2 rounded-full bg-orange animate-bounce"></div>
                      <div className="h-2 w-2 rounded-full bg-orange animate-bounce" style={{ animationDelay: "0.2s" }}></div>
                      <div className="h-2 w-2 rounded-full bg-orange animate-bounce" style={{ animationDelay: "0.4s" }}></div>
                    </div>
                    <p className="text-sm text-gray-500">
                      Running AI analysis...
                    </p>
                  </div>

                  {/* Cancel Button */}
                  <div className="flex justify-center">
                    <SimpleButton
                      text="Cancel"
                      colour="red"
                      onClick={onCancel}
                      className="min-w-[140px]"
                    />
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
